import type { FC, ReactNode } from 'react'
import type { CurtainHandle, CurtainMeta } from '@/components/motion/curtain'
import type { NavigateOptions, TransitionState } from '@/components/motion/transition-context'

import { useRouter } from 'next/router'
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { getLenis, useLenis } from '@/components/motion/smooth-scroll'
import { TransitionContext } from '@/components/motion/transition-context'
import Curtain from '@/components/motion/curtain'
import { navLinks, siteConfig } from '@/constants/site'

interface TransitionProviderProps {
    children: ReactNode
}

const MIN_COVER_MS = 1050

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms))

const nextFrame = (): Promise<void> => new Promise(resolve => window.requestAnimationFrame(() => resolve()))

const resolveMeta = (pathname: string, label?: string): CurtainMeta => {
    const section = navLinks.find(link => pathname === link.href || pathname.startsWith(`${link.href}/`))

    if (section) return { label: label || section.label, index: section.index }

    return { label: label || siteConfig.name, index: pathname === '/' ? '00' : '—' }
}

const headerOffset = (): number => {
    const value = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'))

    return Number.isFinite(value) ? value : 72
}

/** Scroll to an in-page anchor, or to the top when there is none. */
export const scrollToTarget = (hash: string | null, immediate = false): void => {
    const lenis = getLenis()
    const element = hash ? document.getElementById(decodeURIComponent(hash.replace(/^#/, ''))) : null

    if (element) {
        if (lenis) lenis.scrollTo(element, { immediate, force: true, offset: -headerOffset(), duration: 1.4 })
        else element.scrollIntoView({ behavior: immediate ? 'auto' : 'smooth' })

        return
    }

    if (lenis) lenis.scrollTo(0, { immediate, force: true, duration: 1.4 })
    else window.scrollTo({ top: 0, behavior: immediate ? 'auto' : 'smooth' })
}

const TransitionProvider: FC<TransitionProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const curtain = useRef<CurtainHandle>(null)
    const busy = useRef(true)
    const [ready, setReady] = useState(false)
    const [navigating, setNavigating] = useState(false)
    const [enterDelay, setEnterDelay] = useState(0.1)

    const finish = useCallback((): void => {
        busy.current = false
        setNavigating(false)
        document.documentElement.classList.remove('is-transitioning')
        getLenis()?.start()
        ScrollTrigger.refresh()
    }, [])

    useEffect(() => {
        if (lenis && busy.current) lenis.stop()
    }, [lenis])

    useEffect(() => {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'

        document.documentElement.classList.add('is-transitioning')

        let cancelled = false

        const start = async (): Promise<void> => {
            try {
                await Promise.race([document.fonts?.ready ?? Promise.resolve(), wait(1500)])
            } catch {
                // Fonts failing to load must never block the site.
            }

            if (cancelled) return

            if (window.location.hash) scrollToTarget(window.location.hash, true)

            ScrollTrigger.refresh()

            const reveal = (): void => {
                setEnterDelay(0.1)
                setReady(true)
            }

            if (curtain.current) await curtain.current.intro(reveal)
            else reveal()

            if (!cancelled) finish()
        }

        start()

        return () => {
            cancelled = true
        }
    }, [finish])

    const navigate = useCallback(async (href: string, options: NavigateOptions = {}): Promise<void> => {
        const url = new URL(href, window.location.href)
        const here = new URL(router.asPath, window.location.href)
        const target = `${url.pathname}${url.search}${url.hash}`

        if (url.pathname === here.pathname && url.search === here.search) {
            if (busy.current) return

            scrollToTarget(url.hash || null)

            if (url.hash !== here.hash && !options.pop) window.history.replaceState(window.history.state, '', target)

            return
        }

        if (busy.current) return

        busy.current = true
        setNavigating(true)
        setReady(false)
        document.documentElement.classList.add('is-transitioning')
        getLenis()?.stop()

        const startedAt = performance.now()

        if (curtain.current) await curtain.current.cover(resolveMeta(url.pathname, options.label))

        try {
            if (options.pop) await router.replace(target, undefined, { scroll: false })
            else await router.push(target, undefined, { scroll: false })
        } catch {
            // A cancelled route change still has to lift the curtain.
        }

        await nextFrame()
        await nextFrame()

        const page = document.querySelector<HTMLElement>('[data-page]')

        if (page) gsap.set(page, { clearProps: 'transform' })

        scrollToTarget(url.hash || null, true)
        ScrollTrigger.refresh()

        const hold = MIN_COVER_MS - (performance.now() - startedAt)

        if (hold > 0) await wait(hold)

        const reveal = (): void => {
            setEnterDelay(0.25)
            setReady(true)
        }

        if (curtain.current) await curtain.current.reveal(reveal)
        else reveal()

        finish()
    }, [router, finish])

    const navigateRef = useRef(navigate)

    useEffect(() => {
        navigateRef.current = navigate
    }, [navigate])

    useEffect(() => {
        const onClick = (event: MouseEvent): void => {
            if (event.defaultPrevented || event.button !== 0) return
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

            const anchor = (event.target as Element | null)?.closest?.('a')

            if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) return
            if (anchor.target && anchor.target !== '_self') return
            if (anchor.hasAttribute('download') || anchor.dataset.transition === 'off') return

            const url = new URL(anchor.href)

            if (url.origin !== window.location.origin) return
            if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/_next/')) return

            event.preventDefault()
            navigateRef.current(`${url.pathname}${url.search}${url.hash}`, { label: anchor.dataset.transitionLabel })
        }

        document.addEventListener('click', onClick, true)

        return () => document.removeEventListener('click', onClick, true)
    }, [])

    useEffect(() => {
        router.beforePopState(({ as }) => {
            if (busy.current) return true

            navigateRef.current(as, { pop: true })

            return false
        })

        return () => router.beforePopState(() => true)
    }, [router])

    useEffect(() => {
        const refresh = (): void => ScrollTrigger.refresh()

        document.fonts?.ready.then(refresh).catch(() => undefined)
        window.addEventListener('load', refresh)

        return () => window.removeEventListener('load', refresh)
    }, [])

    const value = useMemo<TransitionState>(() => ({
        ready,
        enterDelay,
        navigating,
        navigate: (href: string, options?: NavigateOptions) => {
            navigateRef.current(href, options)
        }
    }), [ready, enterDelay, navigating])

    return (
        <TransitionContext.Provider value={value}>
            {children}

            <Curtain ref={curtain} />
        </TransitionContext.Provider>
    )
}

export default TransitionProvider
